import React from 'react';

import {Link} from 'react-router';

import {MultipleAssignedAvatar} from 'app/components/general/avatars/MultipleAssignedAvatar';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {DocumentIconWithNotifications} from 'app/notifly/components/DocumentIconWithNotifications';
import {DashboardItemPresentMembers} from 'app/presence/components/DashboardItemPresentMembers';
import {PercentageBadge} from 'app/tasks/components/TaskPercentageBadge';
import {isOverdue} from 'app/tasks/utils';
import {cannotHoverAndCanTouch} from 'app/utils/browser';
import {gt} from 'app/utils/i18n';

import {CompactTaskItemActionsButton} from './CompactTaskItemActionsButton';
import {StyledSlimTaskItemComponent} from './StyledComponents';

import type {NotificationType} from 'app/notifly/types';
import type {SlimTaskInstanceSerializedType} from 'sweet-types';

interface Props {
  task: SlimTaskInstanceSerializedType;
  notifications?: Array<NotificationType>;
}

export function VeryCompactTaskItem({task, notifications}: Props): JSX.Element {
  const taskIsOverdue = isOverdue(task);
  const assigned_object = task.template.assigned_object;
  const [title, subTitle] = React.useMemo(() => getTitles(task), [task]);

  const areTooltipsEnabled = React.useMemo(() => {
    const usingIE = /Trident\/[67]/.test(window.navigator.userAgent);
    return (
      !usingIE &&
      !cannotHoverAndCanTouch &&
      task.description != null &&
      task.description !== ''
    );
  }, []);

  function addTooltip(content: JSX.Element, subTitle: undefined | string) {
    return areTooltipsEnabled ? (
      <LazyTooltip hide={subTitle == null} tooltip={subTitle}>
        {content}
      </LazyTooltip>
    ) : (
      content
    );
  }

  const assignees = React.useMemo(
    () => [
      ...task.template.users,
      ...task.template.teams,
      ...task.template.roles,
    ],
    [task.template.users, task.template.teams, task.template.roles]
  );

  return (
    <StyledSlimTaskItemComponent
      task={task}
      isOverdue={taskIsOverdue}
      PresenceNode={
        <DashboardItemPresentMembers
          contentType="taskinstance"
          objectId={task.id}
        />
      }
      AssignedNode={
        <MultipleAssignedAvatar
          className="ml-auto ml-sm-0"
          usersAndTeamsAndRoles={assignees}
          tooltipTitle={gt('Assigned to')}
        />
      }
      StatsNode={
        <PercentageBadge task={task} isOverdue={taskIsOverdue} size="3rem" />
      }
      ItemNameNode={addTooltip(
        <div className="text-truncate">
          <Link
            to={task.html_url}
            className="text-dark font-weight-bold d-block d-sm-inline"
          >
            <DocumentIconWithNotifications
              contentType={assigned_object.content_type}
              notifications={notifications}
              className="text-secondary font-weight-light text-secondary"
            />{' '}
            {title}
          </Link>
          {subTitle !== null && (
            <span className="pl-0 pl-sm-2 text-muted font-weight-light font-italic">
              {subTitle}
            </span>
          )}
        </div>,
        subTitle
      )}
      ActionsNode={<CompactTaskItemActionsButton task={task} />}
    />
  );
}

function getTitles(
  task: SlimTaskInstanceSerializedType
): [string, string | undefined] {
  let titles: Array<string> = [];

  if (task.description != null) {
    titles.push(task.description);
  }
  titles.push(task.name);
  return titles as [string, string | undefined];
}
